import { getFirestore, doc, getDoc, getDocFromServer, setDoc, addDoc, collection, serverTimestamp, query, where, orderBy, getDocs, deleteDoc } from "firebase/firestore";
import { getStorage, ref, uploadString, getDownloadURL } from "firebase/storage";
import { app } from "../../firebase-config.js";
import { MediaService } from '../../services/media.service.js';

const db = getFirestore(app);
const storage = getStorage(app);

export const DaVinciService = {
    // --- Series Management (Project Level) ---

    // [NEW] Force Check Status from Server (Bypass Cache)
    checkSeriesStatusFromServer: async (seriesId) => {
        try {
            console.log("[DaVinci] Checking server status for:", seriesId);
            const snap = await getDocFromServer(doc(db, "series", seriesId));
            if (snap.exists()) {
                const data = snap.data();
                console.log("[DaVinci] Server Status:", data.status);
                return data.status;
            }
            return null;
        } catch (e) {
            console.error("[DaVinci] Server Status Check Error:", e);
            // Fallback to cache if offline
            return null;
        }
    },

    // Create or Update Series Metadata (No blocks here)
    // Create or Update Series Metadata (No blocks here)
    saveSeries: async (data, user) => {
        if (!user) return { success: false, error: "User not logged in" };

        try {
            if (data.id) {
                // [UPDATE]
                // Dynamic Payload: Only include fields present in 'data' to avoid overwriting with defaults
                const payload = {
                    updatedAt: serverTimestamp()
                };

                if (data.title !== undefined) payload.title = data.title;
                if (data.status !== undefined) payload.status = data.status;
                if (data.metadata !== undefined) payload.metadata = data.metadata; // Firestore merges map fields if using dot notation, but here we replace the map. 
                // However, DaVinciState usually sends full metadata. 
                // Ideally we should use { merge: true } for the top level doc.

                // CRITICAL: DO NOT include 'authorId' in payload during update to preserve ownership.

                await setDoc(doc(db, "series", data.id), payload, { merge: true });
                return { success: true, id: data.id, isNew: false };
            } else {
                // [CREATE]
                // Full Payload
                const payload = {
                    title: data.title || 'Untitled Series',
                    authorId: user.uid, // Owner = Creator
                    type: 'davinci',
                    status: data.status || 'draft',
                    metadata: data.metadata || {},
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                };

                const newRef = await addDoc(collection(db, "series"), payload);
                return { success: true, id: newRef.id, isNew: true };
            }
        } catch (e) {
            console.error(e);
            return { success: false, error: e.message };
        }
    },

    listMySeries: async (userId) => {
        try {
            const q = query(
                collection(db, "series"),
                where("authorId", "==", userId),
                where("type", "==", "davinci"),
                orderBy("updatedAt", "desc")
            );
            const snaps = await getDocs(q);
            return snaps.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
            console.error(e);
            return [];
        }
    },

    deleteSeries: async (seriesId) => {
        try {
            // TODO: Delete sub-collection episodes (manual or cloud function)
            // For now, just delete parent doc
            await deleteDoc(doc(db, "series", seriesId));
            return { success: true };
        } catch (e) {
            return { success: false, error: e.message };
        }
    },

    // --- Episode Management (Sub-collection) ---

    // List Episodes for a Series (Admin/Author Only - Unfiltered)
    listEpisodes: async (seriesId) => {
        try {
            const q = query(
                collection(db, "series", seriesId, "episodes")
            );
            const snaps = await getDocs(q);
            const list = snaps.docs.map(d => ({ id: d.id, ...d.data() }));

            // [FIX] In-Memory Sort to handle missing 'createdAt' fields
            return list.sort((a, b) => {
                const orderDiff = (a.order || 0) - (b.order || 0);
                if (orderDiff !== 0) return orderDiff;
                const ca = a.createdAt?.seconds || 0;
                const cb = b.createdAt?.seconds || 0;
                return ca - cb;
            });
        } catch (e) {
            console.error(e);
            return [];
        }
    },

    // [SECURE] List Public Episodes (For Viewers/Readers)
    // Filters by status='published' to satisfy Firestore Rules for non-authors
    listPublicEpisodes: async (seriesId) => {
        try {
            const q = query(
                collection(db, "series", seriesId, "episodes"),
                where("status", "==", "published")
                // [FIX] Removed orderBy("order") to include hidden EP2
            );
            const snaps = await getDocs(q);
            const list = snaps.docs.map(d => ({ id: d.id, ...d.data() }));

            // [FIX] "Scientific Sort": Prioritize explicit Order (Index)
            // If order is missing, fallback to Creation Time.
            return list.sort((a, b) => {
                // 1. Order (Primary) - TYPE SAFE
                if (a.order !== undefined && b.order !== undefined) {
                    return Number(a.order) - Number(b.order);
                }
                // 2. CreatedAt (Secondary/Fallback)
                const ca = a.createdAt?.seconds || 0;
                const cb = b.createdAt?.seconds || 0;
                return ca - cb;
            });
        } catch (e) {
            console.error("[DaVinci] Public List Error:", e);
            return []; // Fail gracefully
        }
    },

    // Create or Update Episode
    async saveEpisode(seriesId, episodeData) {
        // episodeData: { id (optional), title, blocks, status }
        const payload = {
            title: episodeData.title || 'Untitled Episode',
            blocks: episodeData.blocks || [],
            status: episodeData.status || 'draft',
            // [FIX] Persist Metadata (ISBN, UCI, etc.)
            metadata: episodeData.metadata || {},
            // [FIX] Persist Thumbnail, BGM and Author Note (Extract from metadata if needed)
            thumbnail: episodeData.thumbnail || episodeData.metadata?.thumbnail || null,
            bgm: episodeData.bgm || episodeData.metadata?.bgm || null,
            authorNote: episodeData.authorNote || episodeData.metadata?.authorNote || null,

            updatedAt: serverTimestamp()
        };

        // [FIX] Prevent overwriting 'order' on edit if not provided
        if (episodeData.order !== undefined) {
            payload.order = episodeData.order;
        } else if (!episodeData.id) {
            // New Episode Default
            payload.order = 1;
        }

        try {
            const epCollection = collection(db, "series", seriesId, "episodes");

            // [GATE LOGIC START]
            let finalStatus = payload.status;
            let isApprovalRequest = false;

            // Only gate if trying to publish
            if (payload.status === 'published') {
                const currentUser = window.user || {};
                const isAdmin = currentUser.role === 'admin' || currentUser.isSuperAdmin;

                // 1. Check strict server status (No Cache)
                let isSeriesLive = false;
                try {
                    // Use 'this' to reference sibling method safe
                    let serverStatus = null;
                    let serverData = null;

                    // Reuse checkSeriesStatusFromServer but we need full data for 'publishedAt' check
                    const snap = await getDocFromServer(doc(db, "series", seriesId));
                    if (snap.exists()) {
                        serverData = snap.data();
                        serverStatus = serverData.status;
                    } else {
                        // Fallback to cache if server fetch fails (offline)
                        const fallbackSnap = await getDoc(doc(db, "series", seriesId));
                        if (fallbackSnap.exists()) {
                            serverData = fallbackSnap.data();
                            serverStatus = serverData.status;
                        }
                    }

                    console.log("[DaVinci] Gate Check - Status:", serverStatus, "PublishedAt:", serverData?.publishedAt);

                    // [GATE LOGIC: "Lifetime Pass"]
                    // 1. Currently Published? -> PASS
                    // 2. Has History (publishedAt exists)? -> PASS (Re-publish Freedom)
                    if (serverStatus === 'published' || (serverData && serverData.publishedAt)) {
                        isSeriesLive = true;
                    }

                } catch (e) {
                    console.error("Gate Check Failed completely", e);
                }

                // 2. Gate Decision
                if (!isSeriesLive && !isAdmin) {
                    // GATE CLOSED: First time publishing requires approval
                    console.log("[DaVinci] First Publication Gate: Enforcing Pending Status.");
                    finalStatus = 'pending';
                    isApprovalRequest = true;

                    // Ensure Series identifies as Pending (Requesting Approval)
                    try {
                        await setDoc(doc(db, "series", seriesId), {
                            status: 'pending',
                            updatedAt: serverTimestamp()
                        }, { merge: true });
                    } catch (e) { console.error("Failed to update series pending status", e); }
                }
            }
            // [GATE LOGIC END]

            // Apply Final Status
            payload.status = finalStatus;

            if (episodeData.id) {
                await setDoc(doc(epCollection, episodeData.id), payload, { merge: true });

                // [SEO/AI] Background Analysis Trigger removed to prevent API cost leak (Autosave Loop)
                // Analysis is now manually triggered or on Publish only.
                // this._triggerTextAnalysis(seriesId, episodeData.id, payload.blocks);

                return { success: true, id: episodeData.id, status: finalStatus, isApprovalRequest };
            } else {
                payload.createdAt = serverTimestamp();
                const newRef = await addDoc(epCollection, payload);

                // [SEO/AI] Auto-analysis removed
                // this._triggerTextAnalysis(seriesId, newRef.id, payload.blocks);

                return { success: true, id: newRef.id, status: finalStatus, isApprovalRequest };
            }
        } catch (e) {
            console.error(e);
            return { success: false, error: e.message };
        }
    },

    // [Public Helper] Trigger Cloud Analysis (Manual/Publish Only)
    async triggerTextAnalysis(seriesId, episodeId, blocks) {
        try {
            // 1. Aggregate Text
            if (!blocks || blocks.length === 0) return;

            let textContent = blocks
                .map(b => {
                    if (b.type === 'text') return b.content;
                    if (b.type === 'dialogue') return `${b.character || 'Someone'}: ${b.content}`;
                    return '';
                })
                .join('\n')
                .trim();

            if (textContent.length < 50) return; // Skip too short

            console.log("[DaVinci] Triggering Background SEO Analysis...");
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const analyze = httpsCallable(functions, 'analyzeEpisodeText');

            // Fire and Log (No Await)
            analyze({
                seriesId,
                episodeId,
                textContent
            }).then(() => console.log("[DaVinci] SEO Analysis Complete"))
                .catch(e => console.warn("[DaVinci] SEO Analysis Failed (Bg):", e));

        } catch (e) {
            console.warn("[DaVinci] SEO Trigger Failed:", e);
        }
    },

    loadEpisode: async (seriesId, episodeId) => {
        try {
            const snap = await getDoc(doc(db, "series", seriesId, "episodes", episodeId));
            if (snap.exists()) return { success: true, data: { id: snap.id, ...snap.data() } };
            return { success: false, error: "Episode not found" };
        } catch (e) {
            return { success: false, error: e.message };
        }
    },

    loadSeries: async (seriesId) => {
        try {
            const snap = await getDoc(doc(db, "series", seriesId));
            if (snap.exists()) return { success: true, data: { id: snap.id, ...snap.data() } };
            return { success: false, error: "Series not found" };
        } catch (e) {
            return { success: false, error: e.message };
        }
    },

    // [NEW] Delete Episode
    deleteEpisode: async (seriesId, episodeId) => {
        try {
            if (!seriesId || !episodeId) return { success: false, error: "Invalid ID" };

            // Simple Delete (No Reordering/Shifting as requested by User)
            // Keeps gaps in the sequence (e.g. 1, 3, 4...)
            await deleteDoc(doc(db, "series", seriesId, "episodes", episodeId));

            return { success: true };
        } catch (e) {
            console.error("Delete Episode Failed:", e);
            return { success: false, error: e.message };
        }
    },

    // --- Image Upload ---
    uploadImage: async (file, pathPrefix) => {
        try {
            // 1. Convert to WebP
            const webpDataUrl = await DaVinciService.convertToWebP(file);

            // 2. Upload to Firebase Storage
            // Path: users/{uid}/davinci/{timestamp}_{filename}.webp
            // We need a unique path. 
            // pathPrefix usually implies scope. Let's use generic logic here.
            const timestamp = Date.now();
            const storagePath = `${pathPrefix}/${timestamp}.webp`; // e.g. "users/123/davinci/series_abc/ep_1"
            const storageRef = ref(storage, storagePath);

            await uploadString(storageRef, webpDataUrl, 'data_url');
            const downloadURL = await getDownloadURL(storageRef);

            return { success: true, url: downloadURL };
        } catch (e) {
            console.error("Upload failed", e);
            return { success: false, error: e.message };
        }
    },

    // Convert Image to WebP
    convertToWebP: (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    const MAX_WIDTH = 1080;
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width;
                        width = MAX_WIDTH;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    resolve(canvas.toDataURL('image/webp', 0.8));
                };
                img.onerror = reject;
            };
            reader.onerror = reject;
        });
    },

    // Process BGM URL (Proxy from MediaService or Utils)
    processBgmUrl: (url) => {
        // Logic from Editor
        if (url.includes('suno.com/s/')) return { error: "Short links not supported" };
        return { url: MediaService.convertToSunoMp3(url) };
    }
};
